import { useState } from 'react';
import { Checkbox } from 'components/ui/checkbox';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import { Skeleton } from 'components/ui/skeleton';
import { AlertCircle, CreditCard, Clock, Database, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrentSubscription } from 'features/legal/api/get-current-subscription';
import { useAcceptCurrentSubscription } from 'features/legal/api/accept-current-subscription';
import { BillingCadence, PlanType } from 'types/api';

export type SubscriptionAcceptanceDialogProps = {
    open: boolean;
    onAccepted: () => void;
};

export const SubscriptionAcceptanceDialog = ({
    open,
    onAccepted,
}: SubscriptionAcceptanceDialogProps) => {
    const [agreed, setAgreed] = useState(false);

    const {
        data: subscription,
        isLoading,
        error,
    } = useCurrentSubscription({
        queryConfig: { enabled: open },
    });

    const acceptMutation = useAcceptCurrentSubscription({
        mutationConfig: {
            onSuccess: () => {
                toast.success('Subscription accepted successfully');
                onAccepted();
            },
            onError: (error) => {
                toast.error('Failed to accept subscription. Please try again.');
                console.error('Failed to accept subscription:', error);
            },
        },
    });

    const handleAccept = () => {
        if (!subscription) {
            toast.error(
                'Subscription information is still loading. Please wait.',
            );
            return;
        }

        acceptMutation.mutate({
            current_subscription_id: subscription.id,
        });
    };

    const formatCurrency = (cents: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(cents / 100);
    };

    const formatBillingCadence = (cadence: BillingCadence) => {
        return cadence === BillingCadence.MONTHLY ? 'month' : 'year';
    };

    const formatPlanType = (planType: PlanType) => {
        switch (planType) {
            case PlanType.PILOT:
                return 'Pilot';
            case PlanType.STARTER:
                return 'Starter';
            case PlanType.PROFESSIONAL:
                return 'Professional';
            case PlanType.ENTERPRISE:
                return 'Enterprise';
            default:
                return planType;
        }
    };

    const formatRenewalStatus = (
        autoRenew: boolean,
        cadence: BillingCadence,
    ) => {
        const duration =
            cadence === BillingCadence.MONTHLY ? 'monthly' : 'yearly';
        return autoRenew
            ? `This ${duration} plan will automatically renew.`
            : `This ${duration} plan will not automatically renew.`;
    };

    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('en-US').format(num);
    };

    const canAccept = agreed && subscription && !acceptMutation.isPending;

    return (
        <Dialog open={open} modal>
            <DialogContent className="max-h-[90vh] max-w-2xl p-0">
                <DialogHeader className="p-6 pb-4">
                    <DialogTitle className="flex items-center gap-2 text-2xl font-semibold">
                        <CreditCard className="h-6 w-6" />
                        Subscription Agreement
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        Please review and accept your subscription terms to
                        continue using Atomix AI.
                    </p>
                </DialogHeader>

                <div className="flex-1 overflow-hidden px-6">
                    {isLoading && (
                        <div className="space-y-4">
                            <Skeleton className="h-20 w-full" />
                            <Skeleton className="h-16 w-full" />
                            <Skeleton className="h-16 w-full" />
                        </div>
                    )}

                    {error && (
                        <Card>
                            <CardContent className="flex items-center justify-center py-8">
                                <div className="flex items-center space-x-2 text-muted-foreground">
                                    <AlertCircle className="h-5 w-5" />
                                    <span>
                                        Failed to load subscription information.
                                        Please try again later.
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {subscription && (
                        <div className="space-y-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <CreditCard className="h-5 w-5" />
                                        Subscription Details
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Plan Type:
                                            </span>
                                            <span className="font-medium">
                                                {formatPlanType(
                                                    subscription.plan_type,
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Billing:
                                            </span>
                                            <span className="font-medium">
                                                {formatCurrency(
                                                    subscription.fee_cents,
                                                )}{' '}
                                                per{' '}
                                                {formatBillingCadence(
                                                    subscription.billing_cadence,
                                                )}
                                            </span>
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                            {formatRenewalStatus(
                                                subscription.auto_renew,
                                                subscription.billing_cadence,
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Zap className="h-5 w-5" />
                                        Compute Costs
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm">
                                        {subscription.user_pays_compute_costs
                                            ? 'Your organization is responsible for all compute costs associated with Project Development and Usage.'
                                            : 'Compute costs are included in your subscription plan.'}
                                    </p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Database className="h-5 w-5" />
                                        Usage Limits
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                                        <div className="flex justify-between">
                                            <span>Projects per month:</span>
                                            <span className="font-medium">
                                                {formatNumber(
                                                    subscription.number_of_projects_per_month,
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>API calls per month:</span>
                                            <span className="font-medium">
                                                {formatNumber(
                                                    subscription.api_inference_calls_per_month,
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Traces per month:</span>
                                            <span className="font-medium">
                                                {formatNumber(
                                                    subscription.traces_per_month,
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Number of users:</span>
                                            <span className="font-medium">
                                                {formatNumber(
                                                    subscription.number_of_users,
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Clock className="h-5 w-5" />
                                        Data Retention
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm">
                                        Traces are retained for{' '}
                                        {subscription.traces_retention_days}{' '}
                                        days.
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </div>

                <div className="border-t p-6 pt-4">
                    <div className="mb-4 flex items-center space-x-2">
                        <Checkbox
                            id="subscription-agreement"
                            checked={agreed}
                            onCheckedChange={(checked) =>
                                setAgreed(checked === true)
                            }
                            disabled={isLoading || !!error}
                        />
                        <label
                            htmlFor="subscription-agreement"
                            className="cursor-pointer text-sm leading-relaxed"
                        >
                            I agree to the subscription terms and conditions
                            outlined above
                        </label>
                    </div>
                    <div className="flex justify-end">
                        <Button
                            onClick={handleAccept}
                            disabled={!canAccept || acceptMutation.isPending}
                            className="min-w-24"
                            loading={acceptMutation.isPending}
                        >
                            Accept Subscription
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
